import { Box, Button, Divider, Flex, Text } from "@chakra-ui/react";
import RecruitingCard from "../../components/SuggestCards/RecrutingCard";
import { useEffect, useState } from "react";
import ContentsDetailBottom from "./ContentsDetailBottom";
import { useNavigate, useParams } from "react-router-dom";
import { contentsDetail } from "../../api";
import CampaignVisibilityControl from "../../components/CampaignVisibilityControl";

export default function ContentsDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [proposal, setProposal] = useState(null);
  const [creators, setCreators] = useState([]);

  // 제안서 상세 조회 API 연동
  useEffect(() => {
    if (!id) return;

    (async () => {
      try {
        setLoading(true);
        const res = await contentsDetail(id);

        setProposal(res.data);
        setCreators(res.data.creators ?? []);
      } catch (e) {
        console.error("proposalDetail error:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) {
    return (
      <Flex h="300px" align="center" justify="center">
        로딩 중...
      </Flex>
    );
  }

  const isCps =
    String(proposal?.campaign?.billingModel ?? "").toUpperCase() === "CPS";
  const visibilityTag = proposal?.campaign?.deletedAt
    ? { label: "숨김", bg: "#E53E3E" }
    : { label: "노출", bg: "#38A169" };

  // 총 비용 계산 (CPUC/CPUR)
  const totalExpectedCost = creators?.reduce((sum, item) => {
    const expected = (item.targetValue ?? 0) * (item.costPerAction ?? 0);
    return sum + expected;
  }, 0);

  // 총 타겟 합계
  const totalTarget = creators?.reduce(
    (sum, item) => sum + (item.targetValue ?? 0),
    0
  );

  const avgCostPerExposure =
    totalTarget > 0 ? totalExpectedCost / totalTarget : 0;

  const platformFeeRate = Number(proposal?.proposal?.platformFeeRate ?? 0);
  const totalManuscriptFeeFromProposal = Number(
    proposal?.proposal?.totalManuscriptFee ?? 0
  );
  const totalManuscriptFeeSum =
    creators?.reduce((sum, c) => sum + Number(c.manuscriptFee || 0), 0) ?? 0;
  const totalManuscriptFee =
    totalManuscriptFeeFromProposal > 0
      ? totalManuscriptFeeFromProposal
      : totalManuscriptFeeSum;
  const totalWithFee = Math.floor(totalManuscriptFee * (1 + platformFeeRate));

  const getCommissionRateText = (c) => {
    const proposalCandidates = [
      proposal?.proposal?.unitPrice,
      proposal?.proposal?.unit_price,
      proposal?.proposal?.commissionRate,
      proposal?.proposal?.costPerAction,
    ];
    const creatorCandidates = [
      c?.unitPrice,
      c?.unit_price,
      c?.commissionRate,
      c?.costPerAction,
    ];
    const effectiveRate =
      creatorCandidates
        .map((v) => Number(v))
        .find((v) => Number.isFinite(v) && v > 0) ??
      proposalCandidates
        .map((v) => Number(v))
        .find((v) => Number.isFinite(v) && v > 0);
    if (!Number.isFinite(effectiveRate) || effectiveRate <= 0) return "미정";
    const pct = Math.round(effectiveRate * 10000) / 100;
    return `${String(pct).replace(/\.00$/, "")}%`;
  };

  return (
    <Box p="40px" bg="white" borderRadius="16px">
      <Flex justifyContent="space-between" mb="16px" align="center">
        <Button variant="outline" onClick={() => navigate("/contents")}>
          목록으로
        </Button>
        <Flex gap="8px" align="center">
          <Box
            bg={visibilityTag.bg}
            color="white"
            px="10px"
            py="3px"
            fontSize="12px"
            borderRadius="8px"
            fontWeight="700"
          >
            {visibilityTag.label}
          </Box>
          <CampaignVisibilityControl
            campaignId={proposal?.campaign?.campaignId ?? proposal?.campaign?.id ?? id}
            hidden={Boolean(proposal?.campaign?.deletedAt)}
            onChanged={(nextHidden) =>
              setProposal((prev) =>
                prev
                  ? {
                      ...prev,
                      campaign: {
                        ...prev.campaign,
                        deletedAt: nextHidden ? new Date().toISOString() : null,
                      },
                    }
                  : prev
              )
            }
          />
        </Flex>
      </Flex>
      <RecruitingCard key={creators.id} item={proposal?.campaign} nobutton />
      <Divider border="1px solid #CAC4D0" my="40px" />
      <Flex gap="40px">
        <Flex w="50%" flexDir="column" gap="20px">
          <Flex gap="4px">
            <Text color="#5D6979" fontSize="16px" fontWeight={500}>
              {proposal.proposal.proposalSentAt?.split("T")[0]}
            </Text>
            <Text color="#0054C0" fontSize="16px" fontWeight={600}>
              {proposal.proposal.proposalSentBy}
            </Text>
          </Flex>
          <Text fontSize="24px" fontWeight={700}>
            {proposal?.proposal.proposalTitle}
          </Text>
          <Flex
            border="1px solid #D1D4D9"
            borderRadius="8px"
            p="12px"
            h="120px"
            flexDir="column"
          >
            <Text color="#5D6979" fontWeight={500} fontSize="16px">
              제안 설명
            </Text>
            <Box
              maxH="160px"
              overflowY="auto"
              pr="6px" // 스크롤바 겹침 방지
              css={{
                "&::-webkit-scrollbar": {
                  width: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#CBD5E0",
                  borderRadius: "4px",
                },
              }}
            >
              <Text fontSize="16px" fontWeight={500} whiteSpace="pre-wrap">
                {proposal.proposal.proposalDescription}
              </Text>
            </Box>
          </Flex>
          {!isCps && (
            <Flex
              gap="2px"
              flexDir="column"
              borderRadius="8px"
              border="1px solid #D1D4D9"
              p="8px"
            >
              <Text color="#5D6979" fontSize="16px" fontWeight={500}>
                {proposal.campaign.billingModel === "CPUC"
                  ? "클릭당 평균 단가"
                  : "노출당 평균 단가"}
              </Text>
              <Text fontSize="16px" fontWeight={600}>
                ₩
                {avgCostPerExposure.toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })}
              </Text>
            </Flex>
          )}
        </Flex>
        <Flex
          w="50%"
          border="1px solid #D1D4D9"
          borderRadius="8px"
          p="16px"
          gap="16px"
          flexDir="column"
        >
          <Text color="#0054C0" fontSize="20px" fontWeight={700}>
            {isCps
              ? "원고료 및 커미션 예상"
              : "캠페인 유형별 KPI 및 예상 비용"}
          </Text>
          {isCps && (
            <Text color="#5D6979" fontSize="14px" fontWeight={700}>
              플랫폼 수수료 {platformFeeRate * 100}%
            </Text>
          )}

          <Flex w="100%" h="100%" overflowY="auto" flexDir="column" gap="16px">
            {proposal?.creators?.map((c) => (
              <Flex key={c.creatorProfileId} w="100%" flexDir="column">
                {!isCps && (
                  <>
                    <Flex
                      w="100%"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Text color="#5D6979" fontSize="16px" fontWeight={500}>
                        {c.username} {proposal.campaign.billingModel}{" "}
                        {proposal.campaign.billingModel === "CPUC"
                          ? "(고유 클릭당 비용)"
                          : "(고유 노출당 비용)"}
                      </Text>
                      <Text fontSize="20px" fontWeight={700}>
                        ₩ {Number(c.costPerAction).toLocaleString()}
                      </Text>
                    </Flex>

                    <Flex
                      w="100%"
                      alignItems="center"
                      justifyContent="space-between"
                      borderBottom="1px solid #EDF2F7"
                      pb="5px"
                    >
                      <Text color="#5D6979" fontSize="16px" fontWeight={500}>
                        예상 비용
                      </Text>
                      <Text fontSize="20px" fontWeight={700}>
                        ₩ {Number(c.targetValue).toLocaleString()}
                      </Text>
                    </Flex>
                  </>
                )}
                {isCps && (
                  <>
                    <Text color="#5D6979" fontSize="16px" fontWeight={500}>
                      {c.username}
                    </Text>
                    <Flex
                      w="100%"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Text color="#5D6979" fontSize="16px" fontWeight={500}>
                        - 원고료
                      </Text>
                      <Text fontSize="20px" fontWeight={700}>
                        ₩ {Number(c.manuscriptFee || 0).toLocaleString()}
                      </Text>
                    </Flex>
                    <Flex
                      w="100%"
                      alignItems="center"
                      justifyContent="space-between"
                      borderBottom="1px solid #EDF2F7"
                      pb="5px"
                    >
                      <Text color="#5D6979" fontSize="16px" fontWeight={500}>
                        - 커미션율
                      </Text>
                      <Text fontSize="20px" fontWeight={700}>
                        {getCommissionRateText(c)}
                      </Text>
                    </Flex>
                  </>
                )}
              </Flex>
            ))}
          </Flex>

          <Divider border="1px solid" />

          {!isCps && (
            <Flex justifyContent="space-between" alignItems="center">
              <Text color="#0054C0" fontSize="16px" fontWeight={700}>
                예상 총 비용
              </Text>
              <Text color="#0054C0" fontSize="20px" fontWeight={700}>
                ₩
                {proposal?.creators
                  ?.reduce((sum, c) => sum + Number(c.targetValue || 0), 0)
                  .toLocaleString()}
              </Text>
            </Flex>
          )}
          {isCps && (
            <>
              <Flex justifyContent="space-between" alignItems="center">
                <Text color="#0054C0" fontSize="16px" fontWeight={700}>
                  원고료 합계
                </Text>
                <Text color="#0054C0" fontSize="20px" fontWeight={700}>
                  ₩{totalManuscriptFee.toLocaleString()}
                </Text>
              </Flex>
              <Flex justifyContent="space-between" alignItems="center">
                <Text color="#0054C0" fontSize="16px" fontWeight={700}>
                  수수료 포함
                </Text>
                <Text color="#0054C0" fontSize="20px" fontWeight={700}>
                  ₩{totalWithFee.toLocaleString()}
                </Text>
              </Flex>
            </>
          )}
        </Flex>
      </Flex>
      <Divider border="1px solid #CAC4D0" my="40px" />
      <ContentsDetailBottom data={creators} />
    </Box>
  );
}
